/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { Attendance } from "../../../../../api";

const attendanceController = new Attendance();

/**
 * Hook personalizado para manejar los datos de asistencia
 * @param {string} accessToken - Token de acceso
 * @param {string} studentId - ID del estudiante
 * @param {string} academicCycleId - ID del ciclo académico
 * @returns {Object} { attendanceData, loading, error }
 */
export const useAttendanceData = (accessToken, studentId, academicCycleId) => {
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadAttendanceHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await attendanceController.getAttendanceHistoryByCycle(
        accessToken,
        studentId,
        academicCycleId
      );
      setAttendanceData(response.data);
      setError("");
    } catch (error) {
      setError("Error al cargar el historial de asistencia");
      console.error("Error loading attendance history:", error);
    } finally {
      setLoading(false);
    }
  }, [accessToken, studentId, academicCycleId]);

  useEffect(() => {
    loadAttendanceHistory();
  }, [loadAttendanceHistory]);

  return { attendanceData, loading, error };
};

/**
 * Hook para manejar la navegación del calendario
 * @param {Object} cycleRange - Rango del ciclo académico
 * @param {string} selectedMonth - Mes seleccionado
 * @param {Function} setSelectedMonth - Función para actualizar el mes
 * @returns {Object} Funciones de navegación
 */
export const useCalendarNavigation = (cycleRange, selectedMonth, setSelectedMonth) => {
  const canNavigatePrevious = useCallback(() => {
    const prevMonth = moment.utc(selectedMonth).local().subtract(1, "month");
    return prevMonth.isSameOrAfter(cycleRange.startDate, "month");
  }, [selectedMonth, cycleRange.startDate]);

  const canNavigateNext = useCallback(() => {
    const nextMonth = moment.utc(selectedMonth).local().add(1, "month");
    return nextMonth.isSameOrBefore(cycleRange.endDate, "month");
  }, [selectedMonth, cycleRange.endDate]);

  const navigatePrevious = useCallback(() => {
    if (canNavigatePrevious()) {
      const prevMonth = moment.utc(selectedMonth).local()
        .subtract(1, "month")
        .format("YYYY-MM");
      setSelectedMonth(prevMonth);
    }
  }, [selectedMonth, canNavigatePrevious, setSelectedMonth]);

  const navigateNext = useCallback(() => {
    if (canNavigateNext()) {
      const nextMonth = moment.utc(selectedMonth).local().add(1, "month").format("YYYY-MM");
      setSelectedMonth(nextMonth);
    }
  }, [selectedMonth, canNavigateNext, setSelectedMonth]);

  return {
    canNavigatePrevious,
    canNavigateNext,
    navigatePrevious,
    navigateNext,
  };
};